/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable, observable, ObservableMap, IReactionDisposer, reaction } from 'mobx';
import * as mobxUtils from 'mobx-utils';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { v4 as UUID } from 'uuid';
import { TrackingKey, BaseTrackingKey, PlanableTrackingKey } from '../ProgramPortfolio/TrackingKeys';
import dayjs from '../../Utils/dayjs';
import { logger } from '../../Utils/logger';
import SentryService from '../../Services/SentryService';
import { ExerciseSetPause, createExerciseSetPause } from './ExerciseSetPause';
import type { ExerciseBlockLog } from './ExerciseBlockLog';

export type ExerciseSet = {
  exerciseBlockLog: ExerciseBlockLog;
  values: ObservableMap<TrackingKey, number>;
  setId: string;
  workoutLogId?: string;
  startDateTime?: Date;
  endDateTime?: Date;
  caloriesBurnt: number;
  elapsedTime: number;
  calculatedDuration: boolean;
  pauses: ExerciseSetPause[];
  elapsedBreak: number;
  elapsedDisposer?: IReactionDisposer;
  elapsedBreakDisposer?: IReactionDisposer;
  readonly lastPause: ExerciseSetPause | undefined;
  readonly calories: number;
  readonly loggedCalories: number | undefined;
  readonly exercise: ExerciseVariation;
  readonly chronologicalIndex: number;
  readonly index: number;
  readonly setNr: number;
  readonly isLastSet: boolean;
  readonly prevSetChronological: ExerciseSet | undefined;
  readonly prevSet: ExerciseSet | undefined;
  readonly nextSetChronological: ExerciseSet | undefined;
  readonly nextSet: ExerciseSet | undefined;
  readonly isCompleted: boolean;
  readonly isFinished: boolean;
  readonly isStarted: boolean;
  readonly isRunning: boolean;
  readonly duration: number;
  readonly breakTime: number;
  readonly totalDuration: number;
  readonly lastPauseEntryStartTimestamp: number;
  readonly isPaused: boolean;
  readonly durationSeconds: number;
  readonly durationFormatted: string;
  readonly totalPauseDuration: number;
  readonly totalDurationPauses: number;
  readonly totalBreakPauses: number;
  readonly loggedBreak: number;
  readonly breakTimeSeconds: number;
  readonly tons: number;
  readonly assignedTrackingKeys: BaseTrackingKey[];
  save(): Promise<any>;
  startElapsedTimeInterval(): void;
  stopElapsedTimeInterval(): void;
  startElapsedBreakTimeInterval(): void;
  stopElapsedBreakTimeInterval(): void;
  pause(): void;
  resume(): void;
  resetZero(): void;
  start(startDateTime?: Date, params?: any, save?: boolean): void;
  stop(
    durationSecondsCalculation?: { durationSeconds: number; calculated: boolean },
    endDateTime?: Date,
    save?: boolean,
  ): ExerciseSet;
  calculateCalories(bmr24: number): number;
  calculateCaloriesTime(bmr24: number, met: number, durationSeconds: number): number;
  setKey(key: TrackingKey, value?: number): void;
  changeIfInvalid(key: BaseTrackingKey): void;
};

export function createExerciseSet(exerciseBlockLog: ExerciseBlockLog, source?: Partial<ExerciseSet>): ExerciseSet {
  return makeAutoObservable({
    exerciseBlockLog,
    values: observable.map((source as any)?.values ?? {}),
    setId: source?.setId ?? UUID(),
    workoutLogId: source?.workoutLogId,
    startDateTime: source?.startDateTime ? new Date(source.startDateTime as any) : undefined,
    endDateTime: source?.endDateTime ? new Date(source.endDateTime as any) : undefined,
    caloriesBurnt: source?.caloriesBurnt ?? 0,
    elapsedTime: 0,
    calculatedDuration: false,
    pauses: (source?.pauses ?? []).map((p: any) => createExerciseSetPause(p)),
    elapsedBreak: 0,
    elapsedDisposer: undefined as IReactionDisposer | undefined,
    elapsedBreakDisposer: undefined as IReactionDisposer | undefined,

    save() {
      return this.exerciseBlockLog.workoutLog.save();
    },

    startElapsedTimeInterval() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = reaction(
        () => mobxUtils.now(40),
        (timestamp) => {
          if (this.isRunning && timestamp) {
            this.elapsedTime = timestamp - (this.startDateTime?.valueOf() ?? 0);
          }
        },
        { name: 'ExerciseSet::elapsedTime' },
      );
    },

    stopElapsedTimeInterval() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = undefined;
    },

    startElapsedBreakTimeInterval() {
      this.elapsedBreakDisposer?.();
      this.elapsedBreakDisposer = reaction(
        () => mobxUtils.now(40),
        (timestamp: number) => {
          if (this.isFinished && !this.nextSetChronological?.isStarted) {
            this.elapsedBreak = timestamp - (this.endDateTime?.valueOf() ?? this.startDateTime?.valueOf() ?? 0);
          }
        },
        { name: 'ExerciseSet::elapsedBreak' },
      );
    },

    stopElapsedBreakTimeInterval() {
      this.elapsedBreakDisposer?.();
      this.elapsedBreakDisposer = undefined;
    },

    pause() {
      logger('pausing', this.lastPause);
      if (!this.lastPause || this.lastPause.endDateTime) {
        this.pauses.push(createExerciseSetPause());
      }
    },

    resume() {
      logger('resuming', this.lastPause);
      if (this.lastPause) {
        this.lastPause.endDateTime = new Date();
      }
    },

    resetZero() {
      if (!this.endDateTime && this.startDateTime) {
        this.startDateTime = new Date();
        this.pauses = [];
      }
    },

    start(startDateTime?: Date, params: any = {}, save: boolean = true) {
      if (!this.isStarted) {
        if (this.prevSetChronological) {
          logger(
            `Setting break ${this.prevSetChronological.exerciseBlockLog.exercise.name} ${this.prevSetChronological.breakTime / 1000}`,
          );
          this.prevSetChronological.setKey('BREAK', this.prevSetChronological.breakTime / 1000);
          this.prevSetChronological.stopElapsedBreakTimeInterval();
        }

        this.startDateTime = startDateTime ?? new Date();
        this.startElapsedTimeInterval();
        save && this.save();

        SentryService.leaveBreadcrumb('ExerciseStarted', {
          variationId: this.exercise.id,
          sourceType: this.exercise.sourceType,
          sourceId: this.exercise.sourceId,
          setId: this.setId,
          workoutLogId: this.workoutLogId,
          startTimestamp: this.startDateTime,
          ...params,
        });
      }
    },

    stop(
      durationSecondsCalculation?: { durationSeconds: number; calculated: boolean },
      endDateTime: Date = new Date(),
      save: boolean = true,
    ): ExerciseSet {
      this.lastPause?.stop();
      this.changeIfInvalid('WEIGHT');
      this.changeIfInvalid('REPETITIONS');

      if (this.isRunning) {
        this.endDateTime = endDateTime;
        this.stopElapsedTimeInterval();
        this.startElapsedBreakTimeInterval();
        this.calculatedDuration = durationSecondsCalculation ? durationSecondsCalculation.calculated : false;
        const setDuration =
          durationSecondsCalculation?.durationSeconds ||
          (this.endDateTime.valueOf() -
            (this.startDateTime?.valueOf() ?? this.endDateTime.valueOf()) -
            this.totalPauseDuration) /
            1000;
        this.values.set('DURATION', setDuration);

        save && this.save();

        SentryService.addBreadcrumb('ExerciseStopped', {
          setId: this.setId,
          workoutLogId: this.workoutLogId,
          endTimestamp: this.endDateTime,
        });
      }

      return this;
    },

    calculateCalories(bmr24: number): number {
      const met = this.exercise.met > 0 ? this.exercise.met : 5.5;
      const breakCalories = this.nextSetChronological
        ? this.calculateCaloriesTime(bmr24, 5.5, this.breakTime / 1000.0)
        : 0;
      const exerciseCalories = this.calculateCaloriesTime(bmr24, met, this.durationSeconds);
      return exerciseCalories + breakCalories;
    },

    calculateCaloriesTime(bmr24: number, met: number, durationSeconds: number): number {
      if (this.loggedCalories && this.loggedCalories > 0) return this.loggedCalories;
      const timeInHours = Math.max(1, durationSeconds) / 3600.0;
      return Math.round(Math.max(1, Math.round(bmr24 * (met || 5.5) * timeInHours)) * 1.2);
    },

    setKey(key: TrackingKey, value?: number): void {
      if (value || value === 0) {
        this.values.set(key, value);
      }
    },

    changeIfInvalid(key: BaseTrackingKey) {
      const value = this.values.get(key);
      if (!value) {
        const minKey = `MIN_${key}` as PlanableTrackingKey;
        const maxKey = `MAX_${key}` as PlanableTrackingKey;
        const minValue = this.values.get(minKey) || 0;
        const maxValue = this.values.get(maxKey) || 0;
        if (minValue > 0) this.setKey(key, minValue);
        else if (maxValue > 0) this.setKey(key, maxValue);
      }
    },

    get lastPause(): ExerciseSetPause | undefined {
      return this.pauses.length > 0 ? this.pauses[this.pauses.length - 1] : undefined;
    },

    get calories(): number {
      return Math.round(this.loggedCalories || this.caloriesBurnt);
    },

    get loggedCalories(): number | undefined {
      return this.values.get('CALORIES');
    },

    get exercise(): ExerciseVariation {
      return this.exerciseBlockLog.exercise;
    },

    get chronologicalIndex(): number {
      return this.exerciseBlockLog.workoutLog.sets.findIndex((s) => s.setId === this.setId);
    },

    get index(): number {
      return this.exerciseBlockLog ? this.exerciseBlockLog.sets.findIndex((s) => s.setId === this.setId) : -1;
    },

    get setNr(): number {
      return this.index + 1;
    },

    get isLastSet(): boolean {
      return this.index + 1 >= this.exerciseBlockLog.sets.length;
    },

    get prevSetChronological(): ExerciseSet | undefined {
      if (this.chronologicalIndex > 0) {
        return this.exerciseBlockLog.workoutLog.sets[this.chronologicalIndex - 1];
      }
      return undefined;
    },

    get prevSet(): ExerciseSet | undefined {
      if (this.index === 0) return this.exerciseBlockLog.prevBlockLog?.prevSet;
      return this.exerciseBlockLog.sets[this.index - 1];
    },

    get nextSetChronological(): ExerciseSet | undefined {
      if (this.chronologicalIndex !== -1) {
        const nextIndex = this.chronologicalIndex + 1;
        return nextIndex < this.exerciseBlockLog.workoutLog.sets.length
          ? this.exerciseBlockLog.workoutLog.sets[nextIndex]
          : undefined;
      }
      return undefined;
    },

    get nextSet(): ExerciseSet | undefined {
      if (this.isLastSet) return this.exerciseBlockLog.nextBlockLog?.followingSet;
      const nextIndex = this.index + 1;
      return nextIndex < this.exerciseBlockLog.sets.length ? this.exerciseBlockLog.sets[nextIndex] : undefined;
    },

    get isCompleted(): boolean {
      return this.isFinished && (!this.nextSet || this.nextSet.isStarted);
    },

    get isFinished(): boolean {
      return !!this.endDateTime;
    },

    get isStarted(): boolean {
      return !!this.startDateTime;
    },

    get isRunning(): boolean {
      return this.isStarted && !this.isFinished;
    },

    get duration(): number {
      if (this.isRunning) {
        if (this.isPaused) {
          return Date.now() - (this.startDateTime?.valueOf() ?? Date.now()) - this.totalDurationPauses;
        }
        return this.elapsedTime - this.totalDurationPauses;
      } else if (this.endDateTime) {
        const duration = (this.values.get('DURATION') || 0) * 1000;
        return (
          duration ||
          Math.abs(
            this.endDateTime.valueOf() -
              (this.startDateTime?.valueOf() ?? this.endDateTime.valueOf()) -
              this.totalDurationPauses,
          )
        );
      }
      return 0;
    },

    get breakTime(): number {
      if (this.isFinished) {
        const breakLogged = (this.values.get('BREAK') || 0) * 1000;
        if (this.exerciseBlockLog.workoutLog.active) {
          if (!this.nextSetChronological?.isStarted) {
            if (this.isPaused) {
              return (
                Date.now() -
                (this.endDateTime?.valueOf() ?? this.startDateTime?.valueOf() ?? Date.now()) -
                this.totalBreakPauses
              );
            }
            return (
              this.elapsedBreak - this.totalBreakPauses ||
              Date.now() -
                (this.endDateTime?.valueOf() ?? this.startDateTime?.valueOf() ?? Date.now()) -
                this.totalBreakPauses
            );
          }
          return (
            breakLogged ||
            Math.abs(
              (this.nextSetChronological.startDateTime?.valueOf() ?? this.endDateTime?.valueOf() ?? Date.now()) -
                (this.endDateTime?.valueOf() ?? this.startDateTime?.valueOf() ?? Date.now()) -
                this.totalBreakPauses,
            )
          );
        } else if (this.nextSetChronological) {
          return (
            breakLogged ||
            Math.abs(
              (this.nextSetChronological.startDateTime?.valueOf() ?? Date.now()) -
                (this.endDateTime?.valueOf() ?? this.startDateTime?.valueOf() ?? Date.now()) -
                this.totalBreakPauses,
            )
          );
        }
      }
      return 0;
    },

    get totalDuration(): number {
      return this.duration + (this.nextSetChronological ? this.breakTime : 0);
    },

    get lastPauseEntryStartTimestamp(): number {
      return this.lastPause?.startDateTime.valueOf() ?? 0;
    },

    get isPaused(): boolean {
      return this.pauses.some((p) => p.isPauseRunning);
    },

    get durationSeconds(): number {
      return this.duration / 1000;
    },

    get durationFormatted(): string {
      if (this.duration > 0) {
        if (this.duration > 3599999) return dayjs.utc(this.duration).format('HH:mm:ss');
        return dayjs.utc(this.duration).format('mm:ss');
      }
      return '00:00';
    },

    get totalPauseDuration(): number {
      return this.pauses.reduce((value, s) => s.pauseDuration + value, 0);
    },

    get totalDurationPauses(): number {
      return this.pauses
        .filter((p) => !this.endDateTime || p.startDateTime.valueOf() <= this.endDateTime.valueOf())
        .reduce((total, pause) => pause.pauseDuration + total, 0);
    },

    get totalBreakPauses(): number {
      return this.totalPauseDuration - this.totalDurationPauses;
    },

    get loggedBreak(): number {
      return this.isFinished ? this.values.get('BREAK') || 0 : 0;
    },

    get breakTimeSeconds(): number {
      return this.breakTime / 1000;
    },

    get tons(): number {
      const weight = this.values.get('WEIGHT') || 0;
      const reps = this.values.get('REPETITIONS') || 0;
      return (reps * weight) / 1000;
    },

    get assignedTrackingKeys(): BaseTrackingKey[] {
      const basicKeys: BaseTrackingKey[] = ['REPETITIONS', 'DURATION', 'WEIGHT', 'BREAK', 'CALORIES'];
      return Array.from(this.values.keys())
        .map((item) => (item.startsWith('MIN_') || item.startsWith('MAX_') ? item.substring(4) : item) as BaseTrackingKey)
        .filter((tk) => basicKeys.includes(tk))
        .reduce((result, trackingKey) => {
          if (!result.includes(trackingKey)) {
            result.push(trackingKey);
          }
          return result;
        }, [] as BaseTrackingKey[]);
    },
  } as ExerciseSet);
}
